import type { WebContest } from "./data";

// An empty string on a select means "all"; an empty range bound means no
// bound. Prize bounds are whole dollars, compared against the server's cents.
export type ContestFilters = {
  host: string;
  genre: string;
  challenge: string;
  duration: string;
  prizeMin: string;
  prizeMax: string;
  seatsOpen: boolean;
  myContests: boolean;
  versus: boolean;
  freeAdmission: boolean;
};

export const DEFAULT_FILTERS: ContestFilters = {
  host: "",
  genre: "",
  challenge: "",
  duration: "",
  prizeMin: "",
  prizeMax: "",
  seatsOpen: false,
  myContests: false,
  versus: false,
  freeAdmission: false,
};

export type FilterOptions = { host: string[]; genre: string[]; challenge: string[]; duration: string[] };

function distinct(values: (string | undefined)[]): string[] {
  const seen = new Set<string>();
  for (const v of values) if (v) seen.add(v);
  return [...seen].sort((a, b) => a.localeCompare(b));
}

// Option lists come only from the contests returned, so the rail never offers
// a value that matches nothing.
export function filterOptions(contests: WebContest[]): FilterOptions {
  return {
    host: distinct(contests.map((c) => c.host_username)),
    genre: distinct(contests.map((c) => c.genre)),
    challenge: distinct(contests.map((c) => c.challenge)),
    duration: distinct(contests.map((c) => c.duration)),
  };
}

function bound(value: string): number | null {
  if (value.trim() === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

// The prize a contest's prize filter compares against: the pool or cash
// cents the server sent. A named prize or none has no amount.
const prizeCents = (c: WebContest) => (c.prize.kind === "pool" || c.prize.kind === "cash" ? c.prize.cents ?? 0 : 0);

export function countActiveFilters(f: ContestFilters): number {
  return [
    f.host !== "",
    f.genre !== "",
    f.challenge !== "",
    f.duration !== "",
    f.prizeMin !== "" || f.prizeMax !== "",
    f.seatsOpen,
    f.myContests,
    f.versus,
    f.freeAdmission,
  ].filter(Boolean).length;
}

export function filterContests(contests: WebContest[], f: ContestFilters): WebContest[] {
  const min = bound(f.prizeMin);
  const max = bound(f.prizeMax);
  return contests.filter((c) => {
    if (f.host && c.host_username !== f.host) return false;
    if (f.genre && c.genre !== f.genre) return false;
    if (f.challenge && c.challenge !== f.challenge) return false;
    if (f.duration && c.duration !== f.duration) return false;
    const prize = prizeCents(c);
    if (min !== null && prize < min * 100) return false;
    if (max !== null && prize > max * 100) return false;
    if (f.seatsOpen && (c.state !== "open" || c.seats_taken >= c.seats_total)) return false;
    if (f.myContests && !(c.viewer?.entered || c.viewer?.sponsor)) return false;
    if (f.versus && c.format !== "versus") return false;
    if (f.freeAdmission && c.admission.kind !== "free") return false;
    return true;
  });
}
